import { getOrgAgentsByAgentIdBuilder, postOrgAgentsByAgentIdBuilderRestore } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type { AgentBuilderResponse } from '@onefootprint/request-types/dashboard';
import { parseAsString, useQueryState } from 'nuqs';
import { createContext, type ReactNode, useContext, useEffect, useReducer, useRef } from 'react';
import useCanEditAgent from '@/hooks/use-can-edit-agent';
import useOnReturnToTab from '@/hooks/use-on-return-to-tab';
import useBuilderActions, { type BuilderActions } from '../hooks/use-builder-actions';
import useBuilderMutation, { type AcceptSuggestions, type MutateOp, type RejectSuggestions } from '../hooks/use-builder-mutation';
import { reportsRemovedBy } from './outputs';
import { type Action, initialState, reducer, type State } from './reducer';
import { type SuggestionBuckets, selectSuggestionsByTarget } from './suggestions';
type Ctx = {
  state: State;
  dispatch: React.Dispatch<Action>;
  actions: BuilderActions;
  mutateOp: MutateOp;
  acceptSuggestions: AcceptSuggestions;
  rejectSuggestions: RejectSuggestions;
  suggestionBuckets: SuggestionBuckets;
  applyResponse: (response: AgentBuilderResponse) => void;
  refresh: (opts?: {
    silent?: boolean;
  }) => Promise<void>;
  flushPendingOps: () => Promise<void>;
  discard: (baselineLogId: string) => Promise<AgentBuilderResponse | null>;
  isSavingGraph: boolean;
  canEdit: boolean;
  isReadOnly: boolean;
  jumpTo: (id: string, options?: {
    highlight?: boolean;
  }) => void;
  /** Writes the `versionId` query param, the single source of truth for which version is previewed. */
  setPreviewVersionId: (versionId: string | null) => void;
  restoreVersion: () => Promise<void>;
};
const StoreCtx = createContext<Ctx | null>(null);
type BuilderProviderProps = {
  children: ReactNode;
};
export const BuilderProvider = ({
  children
}: BuilderProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [versionIdParam, setVersionIdParam] = useQueryState('versionId', parseAsString);
  const canEdit = useCanEditAgent();
  const isReadOnly = !canEdit || !!state.current?.isHistoricalPreview;
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  const builderMutation = useBuilderMutation({
    getSession: () => stateRef.current.session,
    getHistory: () => stateRef.current.history,
    dispatch
  });
  const actions = useBuilderActions({
    stateRef,
    dispatch,
    mutateOp: builderMutation.mutateOp,
    restore: builderMutation.restore
  });
  // Percy's edits apply on the server, so a pick on a report one of them removes clears first, as when
  // the author removes it.
  const acceptSuggestions: AcceptSuggestions = suggestionIds => {
    const {
      bundle
    } = stateRef.current;
    const accepted = bundle.suggestions.filter(suggestion => suggestionIds.includes(suggestion.id));
    actions.unpickReports(accepted.flatMap(suggestion => reportsRemovedBy(suggestion.operation, bundle)));
    builderMutation.acceptSuggestions(suggestionIds);
  };
  const highlightTimerRef = useRef<number | null>(null);
  const showRequestErrorToast = useRequestErrorToast();
  const suggestionBuckets = selectSuggestionsByTarget(state.bundle.suggestions, state.bundle.nodes);
  const loadVersion = async (versionId: string): Promise<void> => {
    const agentId = stateRef.current.session?.agentId;
    if (!agentId) return;
    try {
      const {
        data
      } = await getOrgAgentsByAgentIdBuilder({
        path: {
          agentId
        },
        query: {
          versionId
        },
        throwOnError: true
      });
      if (data) builderMutation.applyResponse(data);
    } catch (error) {
      showRequestErrorToast(error);
    }
  };
  const exitPreview = async (): Promise<void> => {
    const agentId = stateRef.current.session?.agentId;
    if (!agentId) return;
    if (!stateRef.current.current?.isHistoricalPreview) return;
    try {
      const {
        data
      } = await getOrgAgentsByAgentIdBuilder({
        path: {
          agentId
        },
        throwOnError: true
      });
      if (data) builderMutation.applyResponse(data);
    } catch (error) {
      showRequestErrorToast(error);
    }
  };

  // `versionId` is the source of truth for the previewed version: the UI only writes it, this
  // effect loads from it. Deps are just the param + session readiness, so it never refires on
  // store churn.
  const sessionReady = !!state.session;
  useEffect(() => {
    if (!sessionReady) return;
    if (versionIdParam) loadVersion(versionIdParam);else exitPreview();
    // loadVersion/exitPreview read the latest state via stateRef, so they're stable here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionIdParam, sessionReady]);
  const restoreVersion: Ctx['restoreVersion'] = async () => {
    if (!canEdit) return;
    const current = stateRef.current.current;
    if (!current?.isHistoricalPreview) return;
    const agentId = stateRef.current.session?.agentId;
    if (!agentId) return;

    // We're previewing a historical version, so headIdRef tracks the previewed id, not the live
    // head. Refetch the live head to supply expectedHeadId; the backend decides whether to undo,
    // redo, or replace-graph based on where the target sits relative to the head.
    try {
      const {
        data: head
      } = await getOrgAgentsByAgentIdBuilder({
        path: {
          agentId
        },
        throwOnError: true
      });
      if (!head) return;
      const {
        data: response
      } = await postOrgAgentsByAgentIdBuilderRestore({
        path: {
          agentId
        },
        body: {
          id: current.id,
          expectedHeadId: head.current.id
        },
        throwOnError: true
      });
      if (response) builderMutation.applyResponse(response);
      // We're back on head; drop the stale preview param so the effect doesn't re-enter preview.
      await setVersionIdParam(null);
    } catch (error) {
      showRequestErrorToast(error);
    }
  };

  // Drains queued saves, then fetches and applies the head snapshot. Returns
  // 'stale' when a save landed while the GET was in flight — applying that
  // snapshot would regress headIdRef (the next save would 409) and revert the
  // saved edit, so it's discarded instead. Skips while previewing a historical
  // version (exitPreview owns that).
  const fetchAndApplyHead = async (): Promise<'applied' | 'stale' | 'skipped'> => {
    await builderMutation.flushPendingOps();
    const agentId = stateRef.current.session?.agentId;
    if (!agentId) return 'skipped';
    if (stateRef.current.current?.isHistoricalPreview) return 'skipped';
    const headBeforeFetch = builderMutation.getHeadId();
    const {
      data
    } = await getOrgAgentsByAgentIdBuilder({
      path: {
        agentId
      },
      throwOnError: true
    });
    if (!data) return 'skipped';
    if (builderMutation.getHeadId() !== headBeforeFetch) return 'stale';
    builderMutation.applyResponse(data);
    return 'applied';
  };

  // Pull the latest head graph + suggestions from the BE. Percy submits suggestions
  // out-of-band (via the chat MCP tools), so we refetch when one may have landed —
  // applyResponse merges over locally-dirty slices, so in-progress edits survive.
  const refresh: Ctx['refresh'] = async ({
    silent = false
  } = {}) => {
    try {
      const result = await fetchAndApplyHead();
      // Retry once so the update that motivated the refresh isn't lost.
      if (result === 'stale') await fetchAndApplyHead();
    } catch (error) {
      if (!silent) showRequestErrorToast(error);
    }
  };

  // The websocket that announces Percy's out-of-band edits may not exist when
  // they land (chat panel closed, sandbox idled out, another tab) — refetch on
  // tab return so they don't require a manual reload.
  useOnReturnToTab(async () => {
    await refresh({
      silent: true
    });
  });
  const jumpTo: Ctx['jumpTo'] = (id, {
    highlight = true
  } = {}) => {
    if (highlightTimerRef.current !== null) {
      window.clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    dispatch({
      type: 'scrollTo',
      id: null
    });
    dispatch({
      type: 'setHighlight',
      id: null
    });
    window.requestAnimationFrame(() => {
      dispatch({
        type: 'scrollTo',
        id
      });
      if (!highlight) return;
      dispatch({
        type: 'setHighlight',
        id
      });
      highlightTimerRef.current = window.setTimeout(() => {
        dispatch({
          type: 'setHighlight',
          id: null
        });
        highlightTimerRef.current = null;
      }, 1500);
    });
  };
  return <StoreCtx.Provider value={{
    state,
    dispatch,
    actions,
    mutateOp: builderMutation.mutateOp,
    acceptSuggestions,
    rejectSuggestions: builderMutation.rejectSuggestions,
    suggestionBuckets,
    applyResponse: builderMutation.applyResponse,
    refresh,
    flushPendingOps: builderMutation.flushPendingOps,
    discard: builderMutation.discard,
    isSavingGraph: builderMutation.isPending,
    canEdit,
    isReadOnly,
    jumpTo,
    setPreviewVersionId: setVersionIdParam,
    restoreVersion
  }}>
      {children}
    </StoreCtx.Provider>;
};
export const useBuilder = () => {
  const ctx = useContext(StoreCtx);
  if (!ctx) {
    throw new Error("useBuilder outside provider");
  }
  return ctx;
};

/** For components that merely enhance their output with builder state (e.g. reference chips) and must also render outside the provider. */
export const useOptionalBuilder = () => {
  return useContext(StoreCtx);
};